// onboardingPresets.js — THE THREE CHOICES of the new-account welcome
// (wt/presets CONTRACT §1 and §5 WIZARD; Nika 2026-10-24: "users should be
// very easily make initial workspaces, by choosing industries, desired apps
// and vibe code new concepts"). The wizard (functions/onboardingWizard.js,
// components/OnboardingWorkStep.js, OnboardingStackStep.js,
// OnboardingIdeaStep.js) and the conversation
// (functions/onboarding.js onboardingPlan) ask the same three, in the same
// order, with the same words:
//
//   work   Your work — an industry IS a Workspace Preset (the store's featured
//          presets, in registry order), or "Something else" (a custom
//          workspace: no preset, the person's own apps)
//   stack  Your apps — the preset's apps ticked (installed ones marked), its
//          suggested apps unticked, the apps the catalog does not carry yet
//          counted (the store sheet's rule); Something else: every App Store
//          app, unticked, the most installed first
//   idea   Your idea — one optional sentence; at Done "Build my app" opens the
//          app builder on it (`/add-app?prompt=<idea>`)
//
// THE DATA IS THE STORE'S (packages/marketplace/functions/workspacePresets.js,
// registered at boot through its eager slice): the registry, the copy, who may
// apply, the ONE apply and its live lines (`root._presetApply`). The apps are
// the shell's own App Store read (`root._wsAppsCatalog`,
// functions/workspaceApps.js loadWorkspaceAppsCatalog), named through the one
// store-copy resolver (storeItemText). Nothing here calls the server.
//
// WHO: an owner/admin of the active workspace (workspacePresetCanApply — the
// person making it). Your work and Your apps only while the host's SDK can
// apply presets (`_workspacePresetsUnsupported`); Your idea whatever the SDK.
// A workspace that already carries `settings.preset` (applied at sign-up from
// a `?preset=` link, or a custom answer) shows what it is set up for and
// sends no second apply.
//
// THE PICKS live in the flow (`root._onboardingFlow.picks`):
//   work   '' | a preset key | 'custom'
//   stack  { [appId]: boolean } — the person's changes over the defaults only
//   idea   the sentence
// `stackSent` names the plan the flow sent: ONE apply per flow. Leaving Your
// apps forward sends it in the BACKGROUND (the flow goes on); a close after a
// work pick sends it too ("Skip" never loses an earlier answer). From then on
// Your work and Your apps show what is being set up, and the done step lists
// every line with its retry.
//
// FRANK: every export is self-contained (FA204) — no import, no module
// constant; siblings through `this.call`.

// Is Your work asked, and how? null — not for this person or this host (a
// member; a host without the store's functions or without the SDK's preset
// calls; a registry that offers no featured preset this surface has words
// for — an older server's — so there is nothing to choose, the store's row
// hides the same way); 'applied' — the workspace carries `settings.preset`
// and this flow sent none; 'pick' — choose. While the registry is read (or
// after a failed read) the step stands, and a flow already on it keeps it: a
// step never vanishes under the person. Every input is read first (reactive
// callers).
export function onboardingPresetMode(root) {
  const el = this
  const r = root || (el && el.state && el.state.root) || null
  const fns = (el && el.context && el.context.functions) || {}
  const flow = r && r._onboardingFlow
  const unsupported = !!(r && r._workspacePresetsUnsupported)
  const list = r ? r._workspacePresets : null
  const ws = r && r.workspace && typeof r.workspace === 'object' ? r.workspace : null
  const settings = ws && ws.settings && typeof ws.settings === 'object' ? ws.settings : null
  void (settings && settings.preset)
  void (r && r.org)
  if (!r || !ws) return null
  if (
    typeof fns.workspacePresetCanApply !== 'function' ||
    typeof fns.workspacePresetApplied !== 'function' ||
    typeof fns.workspacePresetApply !== 'function'
  ) {
    return null
  }
  if (unsupported || !el.call('workspacePresetCanApply', r)) return null
  if (flow && flow.stackSent) return 'pick'
  if (el.call('workspacePresetApplied', r)) return 'applied'
  const holding = !!(
    flow &&
    (flow.step === 'work' || flow.step === 'stack' || (flow.picks && flow.picks.work))
  )
  if (Array.isArray(list) && !holding) {
    const featured =
      typeof fns.workspacePresetsFeatured === 'function'
        ? el.call('workspacePresetsFeatured', r) || []
        : []
    const named = featured.some((p) => !!p && !!el.call('workspacePresetText', p.key, 'name'))
    if (!named) return null
  }
  return 'pick'
}

// Is Your idea asked? The person making the workspace — an owner/admin of
// it — whatever the host's SDK (the app builder needs no preset).
export function onboardingIdeaAsked(root) {
  const el = this
  const r = root || (el && el.state && el.state.root) || null
  const fns = (el && el.context && el.context.functions) || {}
  void (r && r.workspace)
  void (r && r.org)
  if (!r || typeof fns.workspacePresetCanApply !== 'function') return false
  return !!el.call('workspacePresetCanApply', r)
}

// The reads the three choices need, asked when the welcome opens (an
// owner/admin only): the preset registry — which also settles whether this
// host's SDK can apply presets — and the App Store's apps, once.
export function onboardingPresetsPrime(root) {
  const el = this
  const r = root || (el && el.state && el.state.root) || null
  const fns = (el && el.context && el.context.functions) || {}
  if (!r || typeof fns.workspacePresetCanApply !== 'function') return false
  if (!el.call('workspacePresetCanApply', r)) return false
  if (typeof fns.workspacePresetsLoad === 'function') {
    Promise.resolve(el.call('workspacePresetsLoad')).catch(() => null)
  }
  if (
    typeof fns.loadWorkspaceAppsCatalog === 'function' &&
    !r._wsAppsCatalogLoaded &&
    !r._wsAppsCatalogLoading
  ) {
    Promise.resolve(el.call('loadWorkspaceAppsCatalog')).catch(() => null)
  }
  return true
}

// The shell's App Store read by `owner/key` (lower-cased — the matching rule
// every catalog read uses): { ready, order: [id], byId: { [id]: { id, title,
// descr, icon, installs } } }. The name goes through the one store-copy resolver
// (the store's own name, in the session language); the icon is the listing's
// (the launcher entry's own), else the house glyph its name reads as.
export function onboardingCatalogApps(root) {
  const el = this
  const r = root || (el && el.state && el.state.root) || null
  const fns = (el && el.context && el.context.functions) || {}
  const list = r && Array.isArray(r._wsAppsCatalog) ? r._wsAppsCatalog : []
  const ready = !!(r && r._wsAppsCatalogLoaded)
  const norm = (v) =>
    String(v || '')
      .trim()
      .toLowerCase()
  const byId = {}
  const order = []
  for (const a of list) {
    if (!a || !a.owner || !a.key) continue
    const id = norm(a.owner) + '/' + norm(a.key)
    if (byId[id]) continue
    const named =
      typeof fns.storeItemText === 'function'
        ? el.call('storeItemText', { key: a.key, name: a.name }, 'name')
        : a.name
    const glyph =
      !a.icon && typeof fns.glyphNameFor === 'function'
        ? el.call('glyphNameFor', { name: a.name, key: a.key }, 'ln')
        : ''
    // the listing's one line about the app (persona round 3, W3: elena —
    // "22 app tiles show one word only, no description"), through the same
    // resolver: the store's own words in the session language, '' for none
    const said =
      typeof fns.storeItemText === 'function'
        ? el.call('storeItemText', { key: a.key, description: a.description }, 'description')
        : a.description
    byId[id] = {
      id,
      title: String(named || a.name || a.key),
      descr: typeof said === 'string' ? said.trim() : '',
      icon: String(a.icon || glyph || ''),
      installs: Number(a.installCount) || 0
    }
    order.push(id)
  }
  return { ready, order, byId }
}

// Your work's tiles: one per FEATURED preset the store has copy for (registry
// order) — its name and its one line — then "Something else". The rest of the
// registry (persona round 3, W3: "More kinds of work") waits behind ONE more
// tile, the last row, across both columns: a press opens them, in registry
// order, before "Something else", and the more tile goes. They show without a
// press too when the held pick is one of them (a pick never hides). Every row
// carries every key a tile reads (a state row falls back to the root's keys).
export function onboardingWorkTiles(root) {
  const el = this
  const r = root || (el && el.state && el.state.root) || null
  const fns = (el && el.context && el.context.functions) || {}
  const flow = r && r._onboardingFlow
  const work = (flow && flow.picks && flow.picks.work) || ''
  const list = r && Array.isArray(r._workspacePresets) ? r._workspacePresets : []
  const featured =
    typeof fns.workspacePresetsFeatured === 'function'
      ? el.call('workspacePresetsFeatured', r) || []
      : []
  const tileOf = (p) => {
    const name = p && el.call('workspacePresetText', p.key, 'name')
    if (!name) return null
    return {
      key: 'work-' + p.key,
      value: p.key,
      label: name,
      hint: el.call('workspacePresetText', p.key, 'line') || '',
      picked: work === p.key,
      custom: false,
      more: false
    }
  }
  const tiles = featured.map(tileOf).filter(Boolean)
  const rank = (p) => (Number.isFinite(Number(p.order)) ? Number(p.order) : 999)
  const others = list
    .filter((p) => p && p.featured !== true && typeof p.key === 'string' && p.key)
    .sort((a, b) => rank(a) - rank(b))
    .map(tileOf)
    .filter(Boolean)
  const open = !!(flow && flow.workMore) || others.some((t) => t.picked)
  if (open) tiles.push(...others)
  tiles.push({
    key: 'work-custom',
    value: 'custom',
    label: 'workspace.onboarding.work.custom',
    hint: 'workspace.onboarding.work.customHint',
    picked: work === 'custom',
    custom: true,
    more: false
  })
  if (!open && others.length) {
    tiles.push({
      key: 'work-more',
      value: '',
      label: 'workspace.onboarding.work.more',
      hint: 'workspace.onboarding.work.moreHint',
      picked: false,
      custom: false,
      more: true
    })
  }
  return tiles
}

// The more tile's press: the rest of the registry shows (held in the flow, so
// a step back and forth keeps it open). Read-only once the flow sent. The
// more tile goes with the press, and the focus with it (persona round 5, sam:
// Enter on "More kinds of work" left the focus on the page body): the first
// newly shown tile takes it (onboardingWorkMoreFocus).
export function onboardingWorkMore() {
  const el = this
  const root = el && el.state && el.state.root
  const flow = root && root._onboardingFlow
  if (!flow || typeof root.update !== 'function' || flow.stackSent || flow.workMore) return false
  const before = (el.call('onboardingWorkTiles', root) || []).map((t) => t && t.value)
  root.update({ _onboardingFlow: { ...flow, workMore: true } }, { preventFetch: true })
  const shown = (el.call('onboardingWorkTiles', root) || []).find(
    (t) => t && t.value && !t.more && before.indexOf(t.value) === -1
  )
  if (shown) el.call('onboardingWorkMoreFocus', shown.value)
  return true
}

// The focus after "More kinds of work": the Your work tile that shows `value`
// (the first one the press opened). Found by the tile's own state row in the
// work step's grid — from the app root, as the pressed tile is gone by then.
// A tile that is not attached or not shown yet (the grid's children land
// after the update, `hide` in a microtask) is tried again on the next frames,
// a few times, then the focus is left alone. Never takes a focus the person
// moved elsewhere in the meantime. → true when the attempt was scheduled.
export function onboardingWorkMoreFocus(value) {
  const el = this
  if (!el || typeof value !== 'string' || !value) return false
  const host = (typeof el.getRoot === 'function' && el.getRoot()) || el
  const find = () => {
    const layer = host && typeof host.lookdown === 'function' && host.lookdown('OnboardingWizard')
    const step =
      layer && typeof layer.lookdown === 'function' && layer.lookdown('OnboardingWorkStep')
    const grid = step && typeof step.lookdown === 'function' && step.lookdown('OnboardingTileGrid')
    const kids = (grid && typeof grid.getChildren === 'function' && grid.getChildren()) || []
    return kids.find((k) => k && k.state && k.state.value === value) || null
  }
  const attempt = (left) => {
    try {
      const tile = find()
      const node = tile && tile.node
      const doc = node && node.ownerDocument
      const active = doc && doc.activeElement
      const free = !active || active === doc.body || !active.isConnected
      if (
        node &&
        node.isConnected &&
        typeof node.focus === 'function' &&
        (free || active === node)
      ) {
        node.focus({ preventScroll: false })
        if (doc.activeElement === node) return
      } else if (node && node.isConnected && !free) return
    } catch (_) {
      /* focus is a courtesy — never an error */
    }
    if (left > 0) {
      if (typeof requestAnimationFrame === 'function')
        requestAnimationFrame(() => attempt(left - 1))
      else setTimeout(() => attempt(left - 1), 16)
    }
  }
  setTimeout(() => attempt(8), 0)
  return true
}

// The icons of a preset's apps on its tile: the apps the catalog carries, in
// install order, six at most ([] before the catalog). Something else wears
// one plus — the apps are the person's to add.
export function onboardingWorkGlyphs(value, root) {
  const el = this
  const r = root || (el && el.state && el.state.root) || null
  void (r && r._wsAppsCatalog)
  void (r && r._workspacePresets)
  if (value === 'custom') return [{ key: 'glyph-0', icon: 'plus', title: '' }]
  if (!value) return [{ key: 'glyph-0', icon: 'chevronDown', title: '' }]
  const preset = value ? el.call('workspacePresetByKey', value, r) : null
  if (!preset) return []
  const byId = (el.call('onboardingCatalogApps', r) || {}).byId || {}
  const out = []
  for (const id of preset.apps || []) {
    const app = byId[String(id || '').toLowerCase()]
    if (app && out.length < 6) {
      out.push({ key: 'glyph-' + out.length, icon: app.icon, title: app.title })
    }
  }
  return out
}

// THE WORK PICK — held in the flow (sent with Your apps). A new pick starts
// Your apps from its defaults. `opts.toggle` (a tile's press): pressing the
// picked tile again takes the pick back. Read-only once the flow sent.
export function onboardingWorkPick(value, opts) {
  const el = this
  const root = el && el.state && el.state.root
  const flow = root && root._onboardingFlow
  if (!flow || typeof root.update !== 'function' || flow.stackSent) return false
  if (typeof value !== 'string' || !value) return false
  const picks = flow.picks && typeof flow.picks === 'object' ? flow.picks : {}
  const toggle = !!(opts && opts.toggle)
  const next = toggle && picks.work === value ? '' : value
  const same = picks.work === next
  root.update(
    {
      _onboardingFlow: {
        ...flow,
        progressed: true,
        picks: { ...picks, work: next, stack: same ? picks.stack || {} : {} }
      }
    },
    { preventFetch: true }
  )
  return true
}

// The conversation's answer to Your work: the pick, sent at once with the
// preset's own defaults (the conversation has no Your apps question) — in the
// background, the conversation goes on.
export function onboardingWorkAnswer(value) {
  const el = this
  const root = el && el.state && el.state.root
  if (!el.call('onboardingWorkPick', value)) return false
  const flow = root && root._onboardingFlow
  if (flow) root.update({ _onboardingFlow: { ...flow, workViaChat: true } }, { preventFetch: true })
  el.call('onboardingStackSend')
  return true
}

// The name a line speaks: the flow's pick (a preset), else the preset the
// workspace is set up with. '' for Something else and for a preset this
// surface has no copy of.
export function onboardingPresetName(root) {
  const el = this
  const r = root || (el && el.state && el.state.root) || null
  const flow = r && r._onboardingFlow
  const work = (flow && flow.picks && flow.picks.work) || ''
  const fns = (el && el.context && el.context.functions) || {}
  if (typeof fns.workspacePresetText !== 'function') return ''
  if (work && work !== 'custom') return el.call('workspacePresetText', work, 'name') || ''
  if (work === 'custom' || typeof fns.workspacePresetApplied !== 'function') return ''
  const applied = el.call('workspacePresetApplied', r)
  const key = applied && applied.key
  return key && key !== 'custom' ? el.call('workspacePresetText', key, 'name') || '' : ''
}

// What Your work shows: `state` 'tiles' (choose), 'applied' (the workspace is
// set up already — the ✓ row, Continue), 'sent' (this flow's apply runs or
// ran — the same row: Setting up <name>… then ✓); `title` / `hint` that row's
// words; `loading` / `failed` the registry's read while there are no tiles.
export function onboardingWorkView(root) {
  const el = this
  const r = root || (el && el.state && el.state.root) || null
  const flow = r && r._onboardingFlow
  const apply = r ? r._presetApply : null
  const list = r ? r._workspacePresets : null
  const error = r ? r._workspacePresetsError : null
  const ws = r && r.workspace && typeof r.workspace === 'object' ? r.workspace : null
  const wsId = ws ? String(ws._id || ws.id || '') : ''
  const t = (k) => String(el.call('polyglot', k) || '')
  const out = {
    mode: null,
    state: 'tiles',
    title: '',
    hint: '',
    done: false,
    loading: false,
    failed: false
  }
  const mode = el.call('onboardingPresetMode', r)
  out.mode = mode
  if (!mode) return out
  const named = (custom, name) => {
    if (custom) return t('workspace.onboarding.work.appliedCustom')
    if (!name) return t('workspace.onboarding.work.appliedOther')
    return t('workspace.onboarding.work.applied').replace('{preset}', name)
  }
  if (mode === 'applied') {
    const applied = el.call('workspacePresetApplied', r) || {}
    const custom = applied.key === 'custom'
    const name = custom ? '' : el.call('workspacePresetText', applied.key, 'name') || ''
    out.state = 'applied'
    out.done = true
    out.title = named(custom, name)
    out.hint = name ? el.call('workspacePresetText', applied.key, 'line') || '' : ''
    return out
  }
  if (flow && flow.stackSent) {
    const key = (flow.picks && flow.picks.work) || ''
    const custom = key === 'custom'
    const name = custom ? '' : el.call('workspacePresetText', key, 'name') || ''
    const mine =
      apply && apply.key === key && String(apply.workspaceId || '') === wsId ? apply : null
    out.state = 'sent'
    if (mine && mine.status === 'error') {
      out.title = t(
        mine.error === 'forbidden'
          ? 'marketplace.preset.errorForbidden'
          : 'marketplace.preset.errorFailed'
      )
      return out
    }
    out.done = !!mine && mine.status === 'done'
    out.title = out.done
      ? named(custom, name)
      : custom
        ? t('workspace.onboarding.work.applyingCustom')
        : t('workspace.onboarding.work.applying').replace('{preset}', name)
    out.hint = name ? el.call('workspacePresetText', key, 'line') || '' : ''
    return out
  }
  out.loading = !Array.isArray(list) && error !== 'failed'
  out.failed = !Array.isArray(list) && error === 'failed'
  return out
}

// Your apps: the rows — { key, id, title, descr, icon, picked, installed,
// suggested, toggleable, hint } — and its counts: `soon` (preset apps the catalog does
// not carry yet: there is no name to show, so they are COUNTED, the store
// sheet's rule), `picked` / `total`, `ready` (the catalog has answered).
// Something else lists every catalog app, the most installed first.
export function onboardingStackView(root) {
  const el = this
  const r = root || (el && el.state && el.state.root) || null
  const fns = (el && el.context && el.context.functions) || {}
  const flow = r && r._onboardingFlow
  const picks = (flow && flow.picks) || {}
  const work = picks.work || ''
  const stack = picks.stack && typeof picks.stack === 'object' ? picks.stack : {}
  const sent = !!(flow && flow.stackSent)
  const ws = r && r.workspace && typeof r.workspace === 'object' ? r.workspace : null
  const settings = (ws && ws.settings && typeof ws.settings === 'object' && ws.settings) || {}
  void (r && r._wsAppsCatalog)
  void (r && r._workspacePresets)
  const out = {
    work,
    custom: work === 'custom',
    ready: false,
    rows: [],
    soon: 0,
    soonText: '',
    picked: 0,
    total: 0,
    countText: ''
  }
  if (!work) return out
  const norm = (v) =>
    String(v || '')
      .trim()
      .toLowerCase()
  const cat = el.call('onboardingCatalogApps', r) || { ready: false, order: [], byId: {} }
  out.ready = !!cat.ready
  const installedIds = (
    (typeof fns.installedWorkspaceAppIds === 'function'
      ? el.call('installedWorkspaceAppIds', settings)
      : null) || []
  ).map(norm)
  // THE PRESET'S OWN WORDS for an app (persona round 5, sam: Your apps read
  // each app's generic store line): the picked preset's
  // `setup.appWords['<owner>/<key>'][lang]`, in the session language, else
  // the store's own line (a registry without the words — an older server's).
  const preset = out.custom ? null : el.call('workspacePresetByKey', work, r)
  const setup = preset && preset.setup && typeof preset.setup === 'object' ? preset.setup : {}
  const appWords =
    setup.appWords && typeof setup.appWords === 'object' && !Array.isArray(setup.appWords)
      ? setup.appWords
      : {}
  const lang = String((typeof fns.getActiveLang === 'function' && el.call('getActiveLang')) || 'en')
  const wordsOf = (id) => {
    for (const k of Object.keys(appWords)) {
      if (norm(k) !== id) continue
      const said = appWords[k] && typeof appWords[k] === 'object' ? appWords[k][lang] : null
      if (typeof said === 'string' && said.trim()) return said.trim()
    }
    return ''
  }
  const row = (id, suggested) => {
    const app = cat.byId[id]
    const installed = installedIds.indexOf(id) !== -1
    const own = Object.prototype.hasOwnProperty.call(stack, id)
    const picked = own ? !!stack[id] : !out.custom && !suggested
    return {
      key: 'stack-' + id.replace(/[^a-z0-9]+/g, '-'),
      id,
      label: '',
      title: app.title,
      descr: wordsOf(id) || app.descr || '',
      icon: app.icon,
      picked: installed || picked,
      installed,
      suggested: !!suggested,
      toggleable: !installed && !sent,
      hint: installed
        ? 'marketplace.preset.appInstalled'
        : suggested
          ? 'marketplace.preset.appSuggested'
          : ''
    }
  }
  if (out.custom) {
    // the most installed first; a tie keeps the catalog's own order
    const ids = cat.order
      .map((id, at) => ({ id, at }))
      .sort((a, b) => cat.byId[b.id].installs - cat.byId[a.id].installs || a.at - b.at)
    for (const { id } of ids) out.rows.push(row(id, false))
  } else {
    const seen = []
    const entries = []
    for (const id of (preset && preset.apps) || []) entries.push({ id: norm(id), suggested: false })
    for (const id of (preset && preset.suggested) || [])
      entries.push({ id: norm(id), suggested: true })
    for (const entry of entries) {
      if (!entry.id || seen.indexOf(entry.id) !== -1) continue
      seen.push(entry.id)
      if (!cat.byId[entry.id]) {
        if (cat.ready) out.soon++
        continue
      }
      out.rows.push(row(entry.id, entry.suggested))
    }
  }
  out.total = out.rows.length
  out.picked = out.rows.filter((x) => x.picked).length
  const t = (k) => String(el.call('polyglot', k) || '')
  out.soonText = !out.soon
    ? ''
    : out.soon === 1
      ? t('marketplace.preset.comingSoonOne')
      : t('marketplace.preset.comingSoon').replace('{count}', String(out.soon))
  out.countText = out.total
    ? t('workspace.onboarding.wizard.appsCount')
        .replace('{n}', String(out.picked))
        .replace('{total}', String(out.total))
    : ''
  return out
}

// Tick or untick one app of Your apps (an app the person may change: not
// installed, nothing sent yet).
export function onboardingStackToggle(id) {
  const el = this
  const root = el && el.state && el.state.root
  const flow = root && root._onboardingFlow
  if (!flow || typeof root.update !== 'function' || flow.stackSent || !id) return false
  const view = el.call('onboardingStackView', root) || { rows: [] }
  const row = (view.rows || []).find((x) => x.id === id)
  if (!row || !row.toggleable) return false
  const picks = flow.picks && typeof flow.picks === 'object' ? flow.picks : {}
  const stack = picks.stack && typeof picks.stack === 'object' ? picks.stack : {}
  root.update(
    {
      _onboardingFlow: {
        ...flow,
        progressed: true,
        picks: { ...picks, stack: { ...stack, [id]: !row.picked } }
      }
    },
    { preventFetch: true }
  )
  return true
}

// THE ONE APPLY the flow's picks ask for — { key, include, exclude }: for a
// preset, its apps the person unticked (catalog rows only: an app the catalog
// does not carry yet stays in the plan and the server reports it) and the
// suggested ones ticked; for Something else, the ticked apps (20 at most, the
// server's cap). An installed app is never named — the apply only adds.
// null without a work pick.
export function onboardingStackPlan(root) {
  const el = this
  const r = root || (el && el.state && el.state.root) || null
  const view = el.call('onboardingStackView', r) || {}
  if (!view.work) return null
  const include = []
  const exclude = []
  for (const row of view.rows || []) {
    if (row.installed) continue
    if (view.custom) {
      if (row.picked) include.push(row.id)
      continue
    }
    if (row.suggested && row.picked) include.push(row.id)
    if (!row.suggested && !row.picked) exclude.push(row.id)
  }
  return { key: view.work, include: include.slice(0, 20), exclude }
}

// SEND the flow's apply — in the background (the flow goes on), once. → the
// apply's promise, or null when there is nothing to send: no work pick, a
// workspace set up already, an apply sent before.
export function onboardingStackSend() {
  const el = this
  const root = el && el.state && el.state.root
  const flow = root && root._onboardingFlow
  if (!flow || typeof root.update !== 'function' || flow.stackSent) return null
  if (el.call('onboardingPresetMode', root) !== 'pick') return null
  const plan = el.call('onboardingStackPlan', root)
  if (!plan || !plan.key) return null
  const sig = [plan.key, plan.include.join(','), plan.exclude.join(',')].join('|')
  const now = root._onboardingFlow || flow
  root.update(
    { _onboardingFlow: { ...now, stackSent: sig, progressed: true } },
    { preventFetch: true }
  )
  const opts =
    plan.key === 'custom'
      ? { include: plan.include }
      : { include: plan.include, exclude: plan.exclude, setup: true }
  return Promise.resolve(el.call('workspacePresetApply', plan.key, opts)).catch(() => null)
}

// TRY AGAIN — the flow's plan once more, after a line failed (the apply only
// adds what is missing, so the installed apps are skipped).
export function onboardingStackRetry() {
  const el = this
  const root = el && el.state && el.state.root
  const flow = root && root._onboardingFlow
  if (!flow || !flow.stackSent) return null
  const view = el.call('onboardingSetupView', root) || {}
  if (!view.retryable) return null
  const plan = el.call('onboardingStackPlan', root)
  if (!plan || !plan.key) return null
  const opts =
    plan.key === 'custom'
      ? { include: plan.include }
      : { include: plan.include, exclude: plan.exclude, setup: true }
  return Promise.resolve(el.call('workspacePresetApply', plan.key, opts)).catch(() => null)
}

// THE LINES of the flow's apply (CONTRACT §1: "every step that writes shows
// its own line, and every failure is a line with a retry"): one per app the
// catalog names — Installing… / Installed / Already there / Coming soon /
// Could not install — the apps it cannot name counted as coming soon, the
// setup's own line (channels and folders), the result line, and whether Try
// again applies. Hidden until the flow sent; hidden for a custom answer with
// no apps (it records the answer and has nothing to show).
//   → { show, status, title, lines: [row], soonText, setupText, errorText,
//       retryable }
export function onboardingSetupView(root) {
  const el = this
  const r = root || (el && el.state && el.state.root) || null
  const flow = r && r._onboardingFlow
  const apply = r ? r._presetApply : null
  const ws = r && r.workspace && typeof r.workspace === 'object' ? r.workspace : null
  const wsId = ws ? String(ws._id || ws.id || '') : ''
  void (r && r._wsAppsCatalog)
  const t = (k) => String(el.call('polyglot', k) || '')
  const out = {
    show: false,
    status: '',
    title: '',
    lines: [],
    soonText: '',
    setupText: '',
    errorText: '',
    retryable: false
  }
  if (!flow || !flow.stackSent) return out
  const key = (flow.picks && flow.picks.work) || ''
  const custom = key === 'custom'
  const mine = apply && apply.key === key && String(apply.workspaceId || '') === wsId ? apply : null
  if (!mine) return out
  out.status = mine.status
  if (mine.status === 'error') {
    out.show = true
    out.errorText = t(
      mine.error === 'forbidden'
        ? 'marketplace.preset.errorForbidden'
        : 'marketplace.preset.errorFailed'
    )
    out.retryable = mine.error === 'failed' || mine.error === 'busy'
    return out
  }
  const byId = (el.call('onboardingCatalogApps', r) || {}).byId || {}
  const STATE_KEYS = {
    pending: 'marketplace.preset.linePending',
    installed: 'marketplace.preset.lineInstalled',
    skipped: 'marketplace.preset.lineSkipped',
    failed: 'marketplace.preset.lineFailed'
  }
  let soon = 0
  let unnamedFailed = 0
  for (const line of Array.isArray(mine.lines) ? mine.lines : []) {
    const id = String((line && line.appId) || '').toLowerCase()
    const app = byId[id]
    if (!app) {
      // no catalog row, so no name: an app this environment does not carry
      // is counted as coming soon; any other failure still asks for a retry
      if (line && line.state === 'failed' && line.error !== 'app_not_found') unnamedFailed++
      else if (line && (line.state === 'pending' || line.state === 'failed')) soon++
      continue
    }
    const state = line.state in STATE_KEYS ? line.state : 'failed'
    const comingSoon = state === 'failed' && line.error === 'app_not_found'
    out.lines.push({
      key: 'line-' + id.replace(/[^a-z0-9]+/g, '-'),
      id,
      title: app.title,
      icon: app.icon,
      state,
      done: state === 'installed' || state === 'skipped',
      failed: state === 'failed' && !comingSoon,
      stateKey: comingSoon ? 'marketplace.preset.lineComingSoon' : STATE_KEYS[state]
    })
  }
  const count = (n, many, one) => (n === 1 ? t(one) : t(many).replace('{count}', String(n)))
  out.soonText = soon
    ? count(soon, 'marketplace.preset.comingSoon', 'marketplace.preset.comingSoonOne')
    : ''
  const setup = mine.result && mine.result.setup
  const failedIn = (part) =>
    setup && setup[part] && Array.isArray(setup[part].failed) ? setup[part].failed.length : 0
  const setupFailed = failedIn('channels') + failedIn('folders')
  const madeIn = (part) =>
    setup && setup[part]
      ? (Array.isArray(setup[part].created) ? setup[part].created.length : 0) +
        (Array.isArray(setup[part].existing) ? setup[part].existing.length : 0)
      : 0
  if (mine.status === 'done') {
    if (setupFailed) {
      out.setupText = count(
        setupFailed,
        'marketplace.preset.setupFailed',
        'marketplace.preset.setupFailedOne'
      )
    } else if (madeIn('channels') + madeIn('folders') > 0) {
      out.setupText = t('marketplace.preset.setupReady')
    }
  }
  out.retryable =
    mine.status === 'done' &&
    (unnamedFailed > 0 || setupFailed > 0 || out.lines.some((l) => l.failed))
  const name = custom ? '' : el.call('workspacePresetText', key, 'name') || ''
  if (mine.status === 'running') out.title = t('marketplace.preset.progressTitle')
  else if (custom) {
    out.title = t(
      out.retryable
        ? 'workspace.onboarding.setup.partialCustom'
        : 'workspace.onboarding.setup.doneCustom'
    )
  } else {
    out.title = t(
      out.retryable ? 'marketplace.preset.donePartial' : 'marketplace.preset.doneLine'
    ).replace('{name}', name)
  }
  out.show = out.lines.length > 0 || soon > 0 || !!out.setupText || out.retryable || !custom
  return out
}

// The welcome leaves (closeOnboarding): the flow's own apply, once it has
// finished, leaves with it — the store sheet's own close rule, so the App
// Store's preset sheet opens on its picker next time. One still running keeps
// going (and keeps its lines).
export function onboardingSetupRelease() {
  const el = this
  const root = el && el.state && el.state.root
  const flow = root && root._onboardingFlow
  if (!flow || !flow.stackSent || typeof root.update !== 'function') return false
  const key = (flow.picks && flow.picks.work) || ''
  const apply = root._presetApply
  if (!apply || apply.key !== key || apply.status === 'running') return false
  root.update({ _presetApply: null })
  return true
}

// Your idea — the sentence, held in the flow: one line (a new line is a
// space), 200 characters at most (Fly's own bound for an idea). Kept as typed
// — trimmed only where it is used. '' clears it.
export function onboardingIdeaSet(text) {
  const el = this
  const root = el && el.state && el.state.root
  const flow = root && root._onboardingFlow
  if (!flow || typeof root.update !== 'function') return false
  const idea = String(text == null ? '' : text)
    .replace(/[\r\n]+/g, ' ')
    .slice(0, 200)
  const picks = flow.picks && typeof flow.picks === 'object' ? flow.picks : {}
  if ((picks.idea || '') === idea) return true
  root.update(
    {
      _onboardingFlow: {
        ...flow,
        progressed: !!(flow.progressed || idea.trim()),
        picks: { ...picks, idea }
      }
    },
    { preventFetch: true }
  )
  return true
}

// THE FIRST WORKSPACE'S NAME — Your work's name field (round 2 BLOCKER #5,
// elena-r2 + sam-r2, 2026-10-26). A direct sign-up's one workspace is named
// by the server's default ("My workspace") and nobody asked for the business
// name; the org gate then asked for it in a "Create team's workspace" dialog
// and founded a SECOND, empty, 0-credit workspace. Now the welcome asks on
// the workspace it opens on, and renames THAT one (functions/
// workspaceRename.js — the settings name card's own write). The field shows
// for whoever sees Your work (an owner/admin — the rename route's roles).
//
// The field's value: the person's draft (`flow.picks.name`, undefined until
// typed), else the held workspace's name (displayName first — the one name
// rule of functions/workspaceRename.js).
export function onboardingNameOf(root) {
  const el = this
  const r = root || (el && el.state && el.state.root) || null
  const flow = r && r._onboardingFlow
  const picks = flow && flow.picks && typeof flow.picks === 'object' ? flow.picks : null
  if (picks && typeof picks.name === 'string') return picks.name
  const ws = r && r.workspace && typeof r.workspace === 'object' ? r.workspace : null
  return ws ? String(ws.displayName || ws.name || '') : ''
}

// Typing in the name field — held in the flow, written when Your work is left
// forward or the welcome closes (onboardingNameSave). A typed name is a pick:
// the flow has progressed (a Skip after it counts as done).
export function onboardingNameSet(text) {
  const el = this
  const root = el && el.state && el.state.root
  const flow = root && root._onboardingFlow
  if (!flow || typeof root.update !== 'function') return false
  const name = String(text == null ? '' : text)
    .replace(/[\r\n]+/g, ' ')
    .slice(0, 60)
  const picks = flow.picks && typeof flow.picks === 'object' ? flow.picks : {}
  if (picks.name === name) return true
  root.update(
    {
      _onboardingFlow: {
        ...flow,
        progressed: !!(flow.progressed || name.trim()),
        picks: { ...picks, name }
      }
    },
    { preventFetch: true }
  )
  return true
}

// WRITE the typed name onto the FIRST workspace — once per name, in the
// background (the flow goes on). Nothing typed, an empty field, or the name
// it already has: no write. A refusal says so (the settings card's own
// sentence) and lets the next leave try again. → the rename's promise, or
// null when there is nothing to send.
export function onboardingNameSave() {
  const el = this
  const root = el && el.state && el.state.root
  const flow = root && root._onboardingFlow
  if (!flow || typeof root.update !== 'function') return null
  const picks = flow.picks && typeof flow.picks === 'object' ? flow.picks : {}
  if (typeof picks.name !== 'string') return null
  const name = picks.name.replace(/\s+/g, ' ').trim()
  const ws = root.workspace && typeof root.workspace === 'object' ? root.workspace : null
  const wsId = ws && (ws.id || ws._id)
  if (!name || !wsId || flow.nameSent === name) return null
  if (String(ws.displayName || ws.name || '') === name) return null
  root.update(
    { _onboardingFlow: { ...(root._onboardingFlow || flow), nameSent: name } },
    { preventFetch: true }
  )
  return Promise.resolve(el.call('workspaceRename', root, wsId, name))
    .catch(() => null)
    .then((out) => {
      if (out && (out.ok || out.reason === 'same')) return out
      const now = root._onboardingFlow
      if (now && now.nameSent === name) {
        root.update({ _onboardingFlow: { ...now, nameSent: null } }, { preventFetch: true })
      }
      el.call('openNotification', {
        title: el.call('polyglot', 'admin.workspaceSettings.nameFailed'),
        type: 'error'
      })
      return out
    })
}

// Your idea's examples — three chips that fill the field (positional keys:
// a key is named by where it sits, never by what it says).
export function onboardingIdeaExamples() {
  return [
    { key: 'idea-1', label: 'workspace.onboarding.idea.example1' },
    { key: 'idea-2', label: 'workspace.onboarding.idea.example2' },
    { key: 'idea-3', label: 'workspace.onboarding.idea.example3' }
  ]
}

// The flow's idea, trimmed ('' when there is none).
export function onboardingIdeaOf(root) {
  const el = this
  const r = root || (el && el.state && el.state.root) || null
  const flow = r && r._onboardingFlow
  return String((flow && flow.picks && flow.picks.idea) || '')
    .replace(/\s+/g, ' ')
    .trim()
}

// The foot's forward word on a step that can be passed by: Your work with no
// pick and Your idea with no sentence read "Skip" (the visible default — the
// workspace stays as it is); every other step reads Continue. A bundle key.
export function onboardingFootNext(root) {
  const el = this
  const r = root || (el && el.state && el.state.root) || null
  const flow = (r && r._onboardingFlow) || {}
  const work = (flow.picks && flow.picks.work) || ''
  const idea = el.call('onboardingIdeaOf', r)
  if (flow.step === 'work') {
    const view = el.call('onboardingWorkView', r) || {}
    if (view.state === 'tiles' && !work) return 'workspace.onboarding.chat.skipQuestion'
  }
  if (flow.step === 'idea' && !idea) return 'workspace.onboarding.chat.skipQuestion'
  return 'workspace.onboarding.wizard.next'
}

// "Build my app": the app builder opens on the idea — a NEW build thread with
// the idea as its first message (pages/add-app.js, functions/addAppPrompt.js).
// FREE TEXT NEVER IN A URL (CONTRACT §1): the idea waits in the builder's own
// park (addAppPromptPark — this tab's sessionStorage, else held on the root
// for this page) and the router goes to the bare `/add-app`, so neither the
// address bar, the history nor a tracker's `location.href` ever holds it.
export function onboardingBuildIdea(text) {
  const el = this
  const idea = String(text || '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 200)
  if (!idea || !el || typeof el.router !== 'function') return false
  const root = (el.state && el.state.root) || null
  if (!el.call('addAppPromptPark', idea, root)) return false
  el.router('/add-app', el.getRoot())
  return true
}
